import { useState, useEffect } from "react";
import { isValidLocaleSegment } from "@/utils/localeRouteHelpers";
import {
  loadSupportedLocales,
  getCachedSupportedLocales,
} from "@/services/supportedLocalesService";

interface IUseSupportedLocaleResult {
  isLocaleValid: boolean;
  isLoading: boolean;
}

/**
 * Hook that validates a URL locale segment against both format rules
 * and the CMS /languages API list of supported locales.
 *
 * - If locale is undefined/empty, it's considered valid (no locale in URL).
 * - If locale fails format check (isValidLocaleSegment), it's invalid immediately.
 * - If locale passes format check, it's validated against the CMS supported locales.
 * - While loading, isLocaleValid defaults to true to avoid flash-of-not-found.
 */
export function useSupportedLocale(
  locale: string | undefined
): IUseSupportedLocaleResult {
  const [supportedLocales, setSupportedLocales] = useState<Set<string> | null>(
    getCachedSupportedLocales()
  );

  useEffect(() => {
    if (getCachedSupportedLocales()) {
      return;
    }

    let isMounted = true;

    void loadSupportedLocales()
      .then((locales) => {
        if (isMounted) {
          setSupportedLocales(locales);
        }
      })
      .catch((error) => {
        console.error("Failed to load supported locales:", error);
      });

    return (): void => {
      isMounted = false;
    };
  }, []);

  // No locale in URL — always valid.
  if (!locale) {
    return { isLocaleValid: true, isLoading: false };
  }

  // Format check fails — immediately invalid.
  if (!isValidLocaleSegment(locale)) {
    return { isLocaleValid: false, isLoading: false };
  }

  // Still loading — assume valid to avoid flash-of-not-found.
  if (!supportedLocales) {
    return { isLocaleValid: true, isLoading: true };
  }

  // Check against the loaded set.
  return {
    isLocaleValid: supportedLocales.has(locale.toLowerCase()),
    isLoading: false,
  };
}
